import { Link } from "@inertiajs/react"
import {
  BarChart3,
  Boxes,
  Container,
  Download,
  FileBarChart2,
  FileText,
  MoreHorizontal,
  Train,
  Truck,
} from "lucide-react"
import { type ComponentType } from "react"

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useOrgRoutes } from "@/hooks/use-org-routes"
import type { ReportSummary } from "@/types"

interface Props {
  report: ReportSummary
}

// The server ships an icon name (string); we look up the actual lucide
// component here. Limit the table to icons we expect to use for reports
// rather than re-exporting all of lucide. Unknown names fall back to a
// neutral default.
const ICON_MAP: Record<string, ComponentType<{ className?: string }>> = {
  BarChart3,
  Container,
  Train,
  Truck,
  Boxes,
  FileBarChart2,
  FileText,
}

// Renders the icon for a report. Lookup happens at render time but the
// component itself is the stable `ReportIcon` — keeps the linter (which
// dislikes "components created during render") happy.
function ReportIcon({
  name,
  className,
}: {
  name: string | null | undefined
  className?: string
}) {
  const Component: ComponentType<{ className?: string }> =
    (name && ICON_MAP[name]) || FileBarChart2
  return <Component className={className} />
}

// Stat strip helpers.
function defaultWindowLabel(report: ReportSummary): string | null {
  // The DSL emits :date_range filters (one spec entry that expands to
  // start/end URL params). The legacy :date kind isn't used anymore;
  // accept both so older specs still match if any reappear.
  const dateFilter = report.filters?.find(
    (f) => f.kind === "date_range" || f.kind === "date",
  )
  if (!dateFilter) return null
  if (dateFilter.kind === "date_range" && dateFilter.default_days) {
    return `Last ${dateFilter.default_days} days`
  }
  return "Last 90 days"
}

function filterSummary(report: ReportSummary): string | null {
  if (!report.filters?.length) return null
  // Group the date filter(s) into one "Date range" chip so we don't show
  // "Arrived from · Arrived through" as two separate chips — they're
  // one concept on the operator's mental model.
  const isDate = (kind: string) => kind === "date" || kind === "date_range"
  const hasDate = report.filters.some((f) => isDate(f.kind))
  const nonDate = report.filters.filter((f) => !isDate(f.kind))
  const parts: string[] = []
  if (hasDate) parts.push("Date range")
  parts.push(...nonDate.map((f) => f.label))
  return parts.join(" · ")
}

export default function ReportCard({ report }: Props) {
  const routes = useOrgRoutes()

  const windowLabel = defaultWindowLabel(report)
  const filtersLabel = filterSummary(report)
  // Interactive matrix pages (downloadable: false) have no flat-row export —
  // hide Download + Send. With no subscriptions either, the whole menu goes.
  const showDownloads = report.downloadable
  const showMenu = showDownloads

  const downloadHref = (format: "csv" | "xlsx" | "pdf") =>
    `${routes.downloadReportPath(report.key)}.${format}`

  return (
    <Card className="flex h-full flex-col">
      <CardHeader>
        <div className="flex min-w-0 items-start gap-3">
          <div className="bg-muted text-muted-foreground flex size-9 shrink-0 items-center justify-center rounded-md">
            <ReportIcon name={report.icon} className="size-5" />
          </div>
          <div className="min-w-0 flex-1">
            <CardTitle className="truncate">{report.title}</CardTitle>
            <p className="text-muted-foreground mt-1 line-clamp-2 text-sm">
              {report.description}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <Button asChild size="sm" className="h-11">
              <Link href={routes.reportPath(report.key)}>Open</Link>
            </Button>
            {showMenu && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="More actions"
                    className="size-11"
                  >
                    <MoreHorizontal className="size-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {showDownloads && (
                    <>
                      <DropdownMenuItem asChild>
                        <a href={downloadHref("csv")}>
                          <Download className="size-4" />
                          Download CSV
                        </a>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <a href={downloadHref("xlsx")}>
                          <Download className="size-4" />
                          Download XLSX
                        </a>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <a href={downloadHref("pdf")}>
                          <Download className="size-4" />
                          Download PDF
                        </a>
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="mt-auto">
        <div className="text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
          {windowLabel && (
            <span className="bg-muted rounded-full px-2 py-0.5">
              {windowLabel}
            </span>
          )}
          <span className="bg-muted rounded-full px-2 py-0.5 tabular-nums">
            {report.row_count.toLocaleString()}{" "}
            {report.row_count === 1 ? "row" : "rows"}
          </span>
          {filtersLabel && (
            <span className="bg-muted rounded-full px-2 py-0.5">
              {filtersLabel}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
